import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';

export default function CashBankDashboard() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      // Codes 1000-1025 are the ordinary cash/bank accounts. `is_payment_source=true`
      // additionally picks up dedicated per-party accounts that function like a bank
      // account (a supplier's own "float") but live outside that code range because
      // they're liabilities, not assets — e.g. 2075 Rajabdeen Haji Float. Fetched
      // separately (not unioned in one query) so each group keeps its own section below.
      const [{ data: standardAccs }, { data: floatAccs }] = await Promise.all([
        supabase.from('chart_of_accounts')
          .select('id, account_code, account_name, account_types(category)')
          .lte('account_code', '1025').gte('account_code', '1000')
          .eq('is_active', true).order('account_code'),
        supabase.from('chart_of_accounts')
          .select('id, account_code, account_name, account_types(category)')
          .eq('is_payment_source', true).eq('is_active', true)
          .or('account_code.gt.1025,account_code.lt.1000')
          .order('account_code'),
      ]);
      const accs = [...(standardAccs || []), ...(floatAccs || [])].map(a => ({ ...a, category: a.account_types?.category || '' }));

      if (!accs.length) { setLoading(false); return; }

      const ids = accs.map(a => a.id);
      const PAGE = 1000;
      let all = [], from = 0;
      while (true) {
        const { data } = await supabase
          .from('journal_lines')
          .select('account_id, base_debit, base_credit, journal_entries!inner(status)')
          .in('account_id', ids)
          .eq('journal_entries.status', 'POSTED')
          // Without a stable order, Postgres doesn't guarantee the same row order between
          // paginated .range() calls — past 1000 rows, lines get skipped or counted twice and
          // the balance silently drifts (Sampath has ~3,800 lines on its own).
          .order('id')
          .range(from, from + PAGE - 1);
        if (!data?.length) break;
        all = all.concat(data);
        if (data.length < PAGE) break;
        from += PAGE;
      }

      // Sign convention matches AccountLedger.js: asset/expense accounts run debit-normal
      // (balance = debit - credit), everything else (liabilities, like a supplier float)
      // runs credit-normal (balance = credit - debit) — so a float account that's gone
      // into an unusual "they owe us" state correctly shows red/negative here instead of
      // a misleadingly large green "available" number.
      const accCategory = {};
      accs.forEach(a => { accCategory[a.id] = a.category; });
      const map = {};
      all.forEach(l => {
        if (!map[l.account_id]) map[l.account_id] = 0;
        const isDebitNormal = accCategory[l.account_id] === 'ASSET' || accCategory[l.account_id] === 'EXPENSE';
        const dr = parseFloat(l.base_debit || 0), cr = parseFloat(l.base_credit || 0);
        map[l.account_id] += isDebitNormal ? (dr - cr) : (cr - dr);
      });

      setAccounts(accs);
      setBalances(map);
      setLoading(false);
    }
    load();
  }, []);

  const totalBalance = accounts.reduce((s, a) => s + (balances[a.id] || 0), 0);
  const cashAccounts = accounts.filter(a => parseInt(a.account_code) >= 1000 && parseInt(a.account_code) < 1005);
  const bankAccounts = accounts.filter(a => parseInt(a.account_code) >= 1005 && parseInt(a.account_code) <= 1025);
  const floatAccounts = accounts.filter(a => parseInt(a.account_code) < 1000 || parseInt(a.account_code) > 1025);

  const AccountCard = ({ acc }) => {
    const bal = balances[acc.id] || 0;
    return (
      <div
        className="card"
        onClick={() => navigate(`/accounts/${acc.id}/ledger`)}
        style={{ cursor: 'pointer', padding: '20px 24px', transition: 'box-shadow 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', marginBottom: 4 }}>{acc.account_code}</div>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>{acc.account_name}</div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--primary)', padding: '2px 8px', background: 'rgba(59,130,246,0.08)', borderRadius: 4 }}>View Ledger</div>
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color: bal >= 0 ? 'var(--success)' : 'var(--danger)' }} className="amount">
          {bal < 0 ? '(' : ''}{Math.abs(bal).toLocaleString(undefined, { maximumFractionDigits: 0 })}{bal < 0 ? ')' : ''}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>LKR</div>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Cash & Bank</div>
          <div className="page-subtitle">All balances from posted journals</div>
        </div>
        <div className="stat-card" style={{ minWidth: 200 }}>
          <div className="stat-label">Total Cash & Bank</div>
          <div className="stat-value amount" style={{ color: totalBalance >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            {totalBalance < 0 ? '(' : ''}{Math.abs(totalBalance).toLocaleString(undefined, { maximumFractionDigits: 0 })}{totalBalance < 0 ? ')' : ''}
          </div>
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        <>
          {bankAccounts.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Bank Accounts</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 28 }}>
                {bankAccounts.map(a => <AccountCard key={a.id} acc={a} />)}
              </div>
            </>
          )}
          {cashAccounts.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Cash</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 28 }}>
                {cashAccounts.map(a => <AccountCard key={a.id} acc={a} />)}
              </div>
            </>
          )}
          {floatAccounts.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Supplier Floats</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {floatAccounts.map(a => <AccountCard key={a.id} acc={a} />)}
              </div>
            </>
          )}
          {accounts.length === 0 && (
            <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
              No cash or bank accounts found (codes 1000–1025)
            </div>
          )}
        </>
      )}
    </div>
  );
}
